// AtriumOwnerHome.tsx — Atrium Owner home, "o-home" (Sprint 83, ADR-082).
// ----------------------------------------------------------------------------
// The Salesforce-style Owner home, composed from the shared home framework and
// fed REAL scoped data from condoStore (the same data Portal.tsx reads). It is
// the new-shell replacement for OwnerPortal when the `atriumRedesign` flag is on.
//
// Data provenance (REAL vs PLACEHOLDER) is documented inline and surfaced in the
// UI: anything not backed by a real source is labelled, never fabricated as a
// confident number. The Sprint 81 fetch-error contract is honoured — a failed
// fetch renders an honest error banner, NOT a false $0.00.
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Result, Modal, Form, Input, Select, Skeleton, Button } from 'antd';
import { staticApi } from '../../../utils/staticApi';
import { resolveOwnerKeys, isOwnRow } from '../../../utils/resolveOwnerKeys';
import {
  MessageOutlined,
  PlusOutlined,
  CreditCardOutlined,
  ToolOutlined,
  DollarOutlined,
  ShopOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { useAuthStore } from '../../../store/authStore';
import { useCondoStore } from '../../../store/condoStore';
import { useAtriumNav } from '../atriumNav';
import type { MaintenanceRequest } from '../../../types';

import { SFGreeting } from './SFGreeting';
import { AtriumAreaChart } from './AreaChart';
import { RailCard } from './RailCard';
import { Assistant, type AssistantItem } from './Assistant';
import { formatDate } from '../../../utils/date';
import './homeStyles.css';
import { currency } from '../../../utils/currency';

// Sprint 88B (M2) — categories mirror MyHomeHub's owner maintenance form.
const REQUEST_CATEGORIES = [
  'Plumbing', 'Electrical', 'HVAC / Heating', 'Appliance', 'Pest Control',
  'Structural', 'Common Area', 'Noise / Disturbance', 'Elevator', 'Other',
];

/** Map a maintenance status to a status-badge tone + label. */
function statusBadge(status: MaintenanceRequest['status']): { tone: string; label: string } {
  switch (status) {
    case 'completed':   return { tone: 'green', label: 'Completed' };
    case 'cancelled':   return { tone: 'gray',  label: 'Cancelled' };
    case 'in_progress':
    case 'assigned':    return { tone: 'blue',  label: 'In progress' };
    default:            return { tone: 'amber', label: 'Submitted' };
  }
}

export const AtriumOwnerHome: React.FC = () => {
  const { user, currentCondo, updateMyProfile } = useAuthStore();
  const {
    getOwnerDashboard,
    fetchFees,
    fetchDocuments,
    fetchPayments,
    fetchMaintenanceRequests,
    fetchAmenities,
    fetchAnnouncements,
    fetchOwners,
    createMaintenanceRequest,
    owners,
    maintenanceRequests,
    amenities,
    announcements,
    payments,
    fees,
    documents,
    statements,
    error,
    loadingByKey,
    errorByKey,
  } = useCondoStore();

  // Sprint 106 (106.2) — gate the balance hero + stat cards on the home's first
  // relevant fetch (fees/owners/payments) so we never flash a confident
  // "$0.00 — you're all paid up" before fees have loaded. A keyed flag is
  // `undefined` until its fetch starts; we treat "not yet started" as loading
  // too (the useEffect fires right after first paint), so the very first render
  // shows a Skeleton, not zeros.
  const RELEVANT_KEYS = ['fees', 'owners', 'payments'] as const;
  const summaryLoading =
    RELEVANT_KEYS.some(k => loadingByKey[k] === true) ||
    RELEVANT_KEYS.every(k => loadingByKey[k] === undefined);
  const summaryError = RELEVANT_KEYS.map(k => errorByKey[k]).find(e => e != null) ?? null;
  // Sprint 116 (116.4) — createMaintenanceRequest is now destructured directly
  // off the typed store (signature accepts Omit<…,'id'|'requestNumber'>, which
  // the backend assigns), so the former `useCondoStore() as any` cast is gone.

  // Sprint 88B (M2) — shell navigator (no-op outside the Atrium shell).
  const { go } = useAtriumNav();

  // Sprint 88B (M2) — owner "New request" flow. The legacy owner maintenance
  // modal lives in MyHomeHub, but the Atrium shell overrides `my-home` with this
  // home — so we host the SAME create flow (store.createMaintenanceRequest) in a
  // lightweight modal here rather than navigating to a dead-end.
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestForm] = Form.useForm();

  // QA F5 — "Contact" CTA opens a lightweight contact-info modal (no backend
  // call). Shows the property-management contact info already on the page.
  const [contactOpen, setContactOpen] = useState(false);

  // Sprint 123.2 — owner profile self-edit. A minimal modal (name/email/phone)
  // wired to the self-scoped store action. The owner can only edit THEIR OWN
  // profile — the form never carries another user's id.
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm] = Form.useForm();

  // QA F5 — "Track" has no owner-reachable destination page (maintenance-hub is
  // board-member+). The owner's requests live in the on-page "My requests"
  // section, so Track scrolls to it instead of a dead nav.
  // Sprint 106 (106.2) — re-fire the home's relevant fetches (retry state).
  const retrySummary = () => {
    if (!currentCondo?.id) return;
    fetchFees(currentCondo.id);
    fetchPayments(currentCondo.id);
    fetchOwners(currentCondo.id);
  };

  const scrollToMyRequests = () => {
    document
      .getElementById('atrium-my-requests')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Same fetch-on-mount / on-condo-change pattern as Portal.tsx. We reuse the
  // EXISTING store actions only — no new fetch logic, no new endpoints. The
  // maintenance / amenities / announcements fetches power the extra Atrium
  // surfaces (My requests, Amenities rail, Announcements rail) that the legacy
  // Portal didn't show; they already exist in the store and are condo-scoped.
  useEffect(() => {
    if (currentCondo?.id) {
      fetchFees(currentCondo.id);
      fetchDocuments(currentCondo.id);
      fetchPayments(currentCondo.id);
      fetchMaintenanceRequests(currentCondo.id);
      fetchAmenities(currentCondo.id);
      fetchAnnouncements(currentCondo.id, true);
      // Sprint 89C — load owners so we can resolve THIS owner's unitNumber for
      // the maintenance-create payload. BUG-1 root cause: submitRequest sent
      // unitNumber:"" (dashboard.owner was undefined because getOwnerById was
      // keyed on the user UUID, not the owner id, and owners were never loaded),
      // so the backend @NotBlank rejected every owner-home submit with HTTP 400.
      fetchOwners(currentCondo.id);
    }
  }, [
    currentCondo?.id,
    fetchFees,
    fetchDocuments,
    fetchPayments,
    fetchMaintenanceRequests,
    fetchAmenities,
    fetchAnnouncements,
    fetchOwners,
  ]);

  // REAL data — identical source to Portal.tsx. currentBalance is the verified
  // ~$4,356 figure once fetchFees has run (Sprint 81). user?.id is null-safe so
  // these derivations can run before the role/linkage guards below.
  const userId = user?.id;
  // Sprint 116 (116.4) — memoize the dashboard derivation. getOwnerDashboard
  // runs O(n) filters over the live fees/payments/documents/statements/owners
  // arrays; without a memo it re-ran on every render (incl. each modal keystroke).
  // The store arrays are intentional deps: the selector reads them via get(), so
  // the snapshot must recompute when any of them changes.
  const dashboard = useMemo(
    () => getOwnerDashboard(userId ?? '', currentCondo?.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getOwnerDashboard, userId, currentCondo?.id, fees, payments, documents, statements, owners],
  );
  const balance = dashboard.currentBalance;
  const owedAmount = balance > 0;

  // Sprint 107 (ADR-100) — ONE owner key. The backend now keys every
  // owner-scoped row (fees, payments, maintenance) on the login-user UUID
  // (== user.id == the login response's `ownerId`). resolveOwnerKeys returns
  // that canonical key; the old numeric Owner.id `?? userId` fallback is gone.
  const ownerKeys = useMemo(() => resolveOwnerKeys(user), [user]);
  const resolvedOwnerId = ownerKeys.key;
  const ownerRecord = useMemo(
    () =>
      owners.find(
        o => o.userId === resolvedOwnerId || o.email === ownerKeys.email,
      ),
    [owners, resolvedOwnerId, ownerKeys.email],
  );

  // REAL — this owner's maintenance requests, matched on the single canonical
  // owner key (BUG-1, "appears in list").
  const myRequests = useMemo(
    () =>
      maintenanceRequests.filter(r => isOwnRow(r.ownerId, user)),
    [maintenanceRequests, user],
  );
  const openRequests = myRequests.filter(
    r => r.status !== 'completed' && r.status !== 'cancelled',
  );

  // REAL — completed payments by this owner in the current calendar year.
  const allOwnerPaymentsThisYear = useMemo(() => {
    const yr = new Date().getFullYear();
    return payments.filter(
      p =>
        isOwnRow(p.ownerId, user) &&
        p.status === 'completed' &&
        new Date(p.paymentDate).getFullYear() === yr,
    );
  }, [payments, user]);
  const paidYtd = allOwnerPaymentsThisYear.reduce((s, p) => s + p.amount, 0);

  // REAL chart data: this owner's completed-payment total per month over the
  // last 6 months. Derived purely from the fetched payments ledger — no mock
  // array. If the owner has no payment history the series is all-zero (honest).
  const { chartPoints, chartLabels, hasChartData } = useMemo(() => {
    const now = new Date();
    const buckets: { label: string; total: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ label: d.toLocaleString('en-CA', { month: 'short' }), total: 0 });
    }
    payments
      .filter(p => isOwnRow(p.ownerId, user) && p.status === 'completed')
      .forEach(p => {
        const d = new Date(p.paymentDate);
        const idx = buckets.findIndex(
          b =>
            b.label ===
            new Date(d.getFullYear(), d.getMonth(), 1).toLocaleString('en-CA', { month: 'short' }),
        );
        // Only count if it falls inside the rolling 6-month window.
        const monthsAgo =
          (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
        if (idx >= 0 && monthsAgo >= 0 && monthsAgo <= 5) buckets[idx].total += p.amount;
      });
    return {
      chartPoints: buckets.map(b => b.total),
      chartLabels: buckets.map(b => b.label),
      hasChartData: buckets.some(b => b.total > 0),
    };
  }, [payments, user]);

  // REAL — available amenities for this condo (count + first few in the rail).
  const amenityList = useMemo(
    () => amenities.filter(a => a.condoId === currentCondo?.id),
    [amenities, currentCondo?.id],
  );

  // REAL — published announcements for this condo (rail card).
  const annList = useMemo(
    // Round-8 BUG-3: residents must not see EXPIRED announcements on the home
    // rail. The Announcements board already hides them (Sprint 104) but this
    // rail (and the Assistant "N new announcement(s)" count derived from it)
    // filtered only by condo, so an AGM notice past its expiresAt still leaked.
    () => announcements
      .filter(a => a.condoId === currentCondo?.id)
      .filter(a => !a.expiresAt || !dayjs(a.expiresAt).isBefore(dayjs()))
      .slice(0, 3),
    [announcements, currentCondo?.id],
  );

  // ── Role / linkage guards (mirror Portal.tsx) ────────────────────────────
  // These MUST come AFTER all hooks above: React requires every hook to run on
  // every render in the same order, so guards that early-return can only appear
  // below the last hook (rules-of-hooks). The derivations above are null-safe.
  if (user?.role !== 'owner') {
    return (
      <Result
        status="403"
        title="Resident home"
        subTitle="This view is only available to unit owners."
        style={{ paddingTop: 60 }}
      />
    );
  }
  if (!user?.ownerId) {
    return (
      <Result
        icon={<FileTextOutlined style={{ color: 'var(--mut2)' }} />}
        title="Your account isn't linked to a unit yet"
        subTitle="A property manager needs to associate this account with your unit before fees, statements, and documents become visible."
        style={{ paddingTop: 60 }}
      />
    );
  }

  // Assistant items derived from REAL signals where they exist.
  const assistantItems: AssistantItem[] = [];
  if (owedAmount) {
    assistantItems.push({
      tone: 'red',
      h: `Balance due: ${currency(balance)}`,
      s: `${dashboard.pendingFees.length} fee(s) outstanding on your account`,
      action: 'Pay',
      // QA F5 — same destination as the "Make a payment" hero button.
      onAction: () => go('payments'),
    });
  }
  openRequests.slice(0, 2).forEach(r =>
    assistantItems.push({
      tone: 'blue',
      h: `Open request: ${r.title}`,
      s: `${r.category} · #${r.requestNumber}`,
      action: 'Track',
      // QA F5 — owners can't reach maintenance-hub; scroll to "My requests".
      onAction: scrollToMyRequests,
    }),
  );
  if (annList.length > 0) {
    assistantItems.push({
      tone: 'violet',
      h: `${annList.length} new announcement(s)`,
      s: 'From your management team',
      action: 'Read',
      // QA F5 — Announcements live in the Communications page (permission: all).
      onAction: () => go('communications'),
    });
  }

  const ownerName = user.name?.split(' ')[0] || 'there';
  // BUG-2 — the condo name is already rendered next to this label, so the
  // fallback must never repeat it. With no unit number, fall back to the condo
  // address if available, else render nothing (no trailing " · X · X").
  const unitLabel = dashboard.owner?.unitNumber
    ? `Unit ${dashboard.owner.unitNumber}`
    : currentCondo?.address || '';

  // Sprint 88B (M2) — submit a new maintenance request via the store action
  // (real API). Mirrors MyHomeHub's OwnerMaintenancePanel payload.
  const submitRequest = async () => {
    try {
      const values = await requestForm.validateFields();
      // Sprint 89C — resolve unitNumber from the owners record (fallback to any
      // existing request's unit) so the backend @NotBlank passes. Was
      // dashboard.owner?.unitNumber, which resolved empty → HTTP 400.
      const unitNumber =
        ownerRecord?.unitNumber ??
        dashboard.owner?.unitNumber ??
        myRequests.find(r => r.unitNumber)?.unitNumber ??
        '';
      if (!unitNumber) {
        staticApi.message.error(
          'Your account isn’t linked to a unit yet — contact your property manager to file a request.',
        );
        return;
      }
      setRequestSubmitting(true);
      await createMaintenanceRequest({
        ...values,
        condoId: currentCondo?.id ?? '',
        unitNumber,
        ownerName: ownerRecord?.name ?? dashboard.owner?.name ?? user.name ?? '',
        // Sprint 107 — single canonical owner key (login-user UUID).
        ownerId: resolvedOwnerId,
        priority: values.priority ?? 'medium',
        status: 'submitted',
        // Sprint 145 (R14-9) — `toISOString()` is UTC, so after 20:00 EDT this
        // stamped TOMORROW's date on a request submitted today. These are local
        // calendar dates; `dayjs()` is local.
        submittedDate: dayjs().format('YYYY-MM-DD'),
        targetDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
        notes: [],
      });
      staticApi.message.success('Maintenance request submitted');
      setRequestOpen(false);
      requestForm.resetFields();
    } catch (err: any) {
      // Sprint 89A — the failure used to be SILENT (modal stayed open, no
      // toast). Surface the server's problem-detail so owners know the submit
      // failed. AntD form-validation rejections carry `errorFields`; for those
      // the inline field errors are already shown, so we skip the toast.
      if (!err?.errorFields) {
        staticApi.message.error(
          err?.message || 'Could not submit your request. Please try again.',
        );
      }
    } finally {
      setRequestSubmitting(false);
    }
  };

  // Sprint 123.2 — open the self-edit modal, prefilling from the current user
  // (name/email) and the linked owner row / user phone.
  const openProfile = () => {
    profileForm.setFieldsValue({
      name: user.name ?? '',
      email: user.email ?? '',
      // Prefer the live user.phone (updated in place by updateMyProfile after a
      // save) over the owners-array mirror, which may not have been refetched.
      phone: user.phone ?? ownerRecord?.phone ?? '',
    });
    setProfileOpen(true);
  };

  // Sprint 123.2 — save the owner's own profile via the self-scoped store
  // action. On success: toast + the merged user re-renders the greeting.
  const saveProfile = async () => {
    let values: { name?: string; email?: string; phone?: string };
    try {
      values = await profileForm.validateFields();
    } catch {
      return; // inline field errors are enough
    }
    setProfileSaving(true);
    try {
      await updateMyProfile({
        name: values.name?.trim() || undefined,
        email: values.email?.trim() || undefined,
        phoneNumber: values.phone?.trim() ?? undefined,
      });
      staticApi.message.success('Profile updated');
      setProfileOpen(false);
    } catch (err: any) {
      staticApi.message.error(
        err?.message || 'Could not update your profile. Please try again.',
      );
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div className="wpage wide fadein">
      <SFGreeting
        hi={`Welcome home, ${ownerName}`}
        badge="OWNER"
        sub={
          <span>
            {currentCondo?.name}
            {unitLabel ? ` · ${unitLabel}` : ''}
          </span>
        }
        btns={
          <>
            <button className="hb ghost" type="button" onClick={openProfile}>
              <UserOutlined /> Edit profile
            </button>
            <button className="hb ghost" type="button" onClick={() => setContactOpen(true)}>
              <MessageOutlined /> Contact
            </button>
            <button className="hb solid" type="button" onClick={() => setRequestOpen(true)}>
              <PlusOutlined /> New request
            </button>
          </>
        }
      />

      {/* Fetch-error contract: surface an honest error rather than a false $0.00. */}
      {error && (
        <div className="home-error" role="alert">
          We couldn't load some of your account data ({error}). The figures below
          may be incomplete — please retry shortly.
        </div>
      )}

      <div className="sfgrid">
        <div className="sfcol">
          {/* Balance hero + stat cards — gated on first load (106.2): never show
              a confident $0/"all paid up" before fees/payments/owners settle.
              116.1 — a labelled region (the visible "Current balance" eyebrow is
              the region's name; aria-label kept stable across the 3 states). */}
          <section className="balance-region" aria-label="Account balance">
          {summaryError ? (
            // A relevant fetch genuinely FAILED — show a retry state, not zeros.
            <div className="balhero" role="alert">
              <div className="bl">Current balance</div>
              <div className="bv num" style={{ fontSize: 22, color: 'var(--mut2)' }}>—</div>
              <div className="bsub">
                We couldn't load your balance ({summaryError}). Please retry.
              </div>
              <div className="bbtns">
                <Button onClick={retrySummary}>Retry</Button>
              </div>
            </div>
          ) : summaryLoading ? (
            <div className="balhero">
              <div className="bl">Current balance</div>
              <Skeleton active title={{ width: 180 }} paragraph={{ rows: 2, width: ['80%', '60%'] }} />
            </div>
          ) : (
            <div className="balhero">
              <div className="bl">Current balance</div>
              <div className="bv num">{currency(balance)}</div>
              <div className="bsub">
                {owedAmount ? (
                  <>You have an outstanding balance across {dashboard.pendingFees.length} fee(s).</>
                ) : (
                  <>You're all paid up. Thank you!</>
                )}
              </div>
              <div className="bbtns">
                <button
                  className="hb solid"
                  type="button"
                  style={{ height: 40 }}
                  onClick={() => go('payments')}
                >
                  <CreditCardOutlined /> Make a payment
                </button>
                <span className="autopay">
                  <span className="dot" /> Autopay status in Payments
                </span>
              </div>
            </div>
          )}
          </section>

          {/* Stat cards — REAL where backed; labelled otherwise. Money/served
              stats are gated on the same first load (106.2). */}
          <section
            className="statrow"
            aria-label="Account summary"
            style={{ gridTemplateColumns: 'repeat(3,1fr)' }}
          >
            <div className="stat">
              <div className="gi"><ToolOutlined /></div>
              <div className="v">{openRequests.length} open</div>
              <div className="l">Service requests</div>
              <div className="d nu">{myRequests.length} total</div>
            </div>
            <div className="stat">
              <div className="gi"><DollarOutlined /></div>
              {summaryError ? (
                <div className="v num" style={{ color: 'var(--mut2)' }}>—</div>
              ) : summaryLoading ? (
                <Skeleton active title={{ width: 90 }} paragraph={false} />
              ) : (
                <div className="v num">{currency(paidYtd)}</div>
              )}
              <div className="l">Paid this year</div>
              <div className="d up">{allOwnerPaymentsThisYear.length} payment(s)</div>
            </div>
            <div className="stat">
              <div className="gi"><ShopOutlined /></div>
              <div className="v">{amenityList.length}</div>
              <div className="l">Amenities available</div>
              <div className="d nu">In your building</div>
            </div>
          </section>

          {/* Performance chart — REAL payments-per-month series */}
          <section className="c p perf" aria-labelledby="o-payments-h">
            <div className="pf-top">
              <div>
                <h2 id="o-payments-h" style={{ fontSize: 14.5, fontWeight: 800, margin: 0 }}>Payments · last 6 months</h2>
                <div className="pf-goal">
                  {hasChartData ? (
                    <>Total paid YTD <b>{currency(paidYtd)}</b></>
                  ) : (
                    <span className="home-note">No payment history yet for this account</span>
                  )}
                </div>
              </div>
            </div>
            <AtriumAreaChart
              points={chartPoints}
              labels={chartLabels}
              color="#1E9E5B"
              valueFormatter={currency}
              ariaLabel={
                hasChartData
                  ? `Your completed payments over the last 6 months: ${chartLabels
                      .map((m, i) => `${m} ${currency(chartPoints[i])}`)
                      .join(', ')}.`
                  : 'Your completed payments over the last 6 months: no payment history yet.'
              }
            />
          </section>

          {/* My requests — REAL maintenance data */}
          <section className="c p" id="atrium-my-requests" aria-labelledby="o-requests-h">
            <div className="ch">
              <h2 id="o-requests-h">My requests</h2>
              <button className="lk" type="button" onClick={() => setRequestOpen(true)}>New request →</button>
            </div>
            {myRequests.length === 0 && (
              <div className="rempty" style={{ padding: '12px 0' }}>
                You have no service requests on file.
              </div>
            )}
            {myRequests.slice(0, 5).map(r => {
              const b = statusBadge(r.status);
              const done = r.status === 'completed';
              return (
                <div className="rec-row" key={r.id}>
                  <span
                    className="ricon"
                    style={{ background: done ? 'var(--ok)' : 'var(--blue)' }}
                  >
                    <ToolOutlined />
                  </span>
                  <div className="rmain">
                    <div className="h">{r.title}</div>
                    <div className="s">{r.category} · #{r.requestNumber}</div>
                  </div>
                  <span className={'bd ' + b.tone}>
                    <span className="pd" />
                    {b.label}
                  </span>
                </div>
              );
            })}
          </section>
        </div>

        {/* Right rail */}
        <div className="sfcol">
          <Assistant items={assistantItems} />

          {/* Announcements — REAL published announcements */}
          <RailCard
            icon={<MessageOutlined />}
            tone="var(--blue)"
            title="Announcements"
            count={annList.length}
          >
            {annList.length === 0 && <div className="rempty">No announcements right now.</div>}
            {annList.map(a => (
              <div className="assist-item" key={a.id}>
                <span className="ad" style={{ background: 'var(--blue)' }} />
                <div className="at">
                  <div className="h">{a.title}</div>
                  <div className="s">
                    {formatDate(a.createdAt)} · {a.category}
                  </div>
                </div>
              </div>
            ))}
          </RailCard>

          {/* Amenities — REAL amenity list */}
          <RailCard icon={<ShopOutlined />} tone="var(--violet)" title="Amenities">
            {amenityList.length === 0 && <div className="rempty">No amenities listed.</div>}
            {amenityList.slice(0, 4).map(a => (
              <div className="evt-item" key={a.id}>
                <div className="ebar" style={{ background: 'var(--ok)' }} />
                <div className="eb">
                  <div className="h">{a.name}</div>
                  <div className="s">{a.location}</div>
                </div>
                <button
                  className="lk"
                  type="button"
                  style={{ alignSelf: 'center' }}
                  // QA F5 — navigate to Operations → Amenities (permission: all).
                  // Specific-amenity preselect isn't supported by the nav, so we
                  // route to the Amenities page.
                  onClick={() => go('amenities')}
                >
                  Book
                </button>
              </div>
            ))}
          </RailCard>
        </div>
      </div>

      {/* Sprint 88B (M2) — New maintenance request modal. Reuses the store's
          createMaintenanceRequest (real API), the same flow as MyHomeHub. */}
      <Modal
        title="New maintenance request"
        open={requestOpen}
        confirmLoading={requestSubmitting}
        onOk={submitRequest}
        onCancel={() => setRequestOpen(false)}
        okText="Submit request"
        destroyOnClose
      >
        <Form form={requestForm} layout="vertical" preserve={false}>
          <Form.Item
            name="title"
            label="Summary"
            rules={[{ required: true, message: 'A short summary is required' }]}
          >
            <Input placeholder="e.g. Kitchen sink is leaking" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Choose a category' }]}
          >
            <Select
              placeholder="Choose a category"
              options={REQUEST_CATEGORIES.map(c => ({ value: c, label: c }))}
            />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Details"
            rules={[{ required: true, message: 'Describe the issue' }]}
          >
            <Input.TextArea rows={4} placeholder="Describe the issue, location, and any access notes." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sprint 123.2 — Owner profile self-edit. Strictly self-scoped: the form
          carries no user id; the store action always targets the logged-in user. */}
      <Modal
        title="Edit your profile"
        open={profileOpen}
        confirmLoading={profileSaving}
        onOk={saveProfile}
        onCancel={() => setProfileOpen(false)}
        okText="Save changes"
        destroyOnClose
      >
        <Form form={profileForm} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Full name"
            rules={[{ required: true, message: 'Your name is required' }]}
          >
            <Input placeholder="e.g. Liam Singh" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'An email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="you@example.com" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ max: 50, message: 'Phone number is too long' }]}
          >
            <Input placeholder="e.g. 416-555-0123" maxLength={50} />
          </Form.Item>
        </Form>
      </Modal>

      {/* QA F5 — Contact modal. Surfaces the property-management contact info
          already available on the page (no backend call). */}
      <Modal
        title="Contact property management"
        open={contactOpen}
        onCancel={() => setContactOpen(false)}
        onOk={() => setContactOpen(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>
          <b>{currentCondo?.name ?? 'Your property'}</b>
        </p>
        {currentCondo?.contactEmail || currentCondo?.contactPhone ? (
          <div>
            {currentCondo?.contactEmail && (
              <p style={{ margin: '4px 0' }}>
                Email:{' '}
                <a href={`mailto:${currentCondo.contactEmail}`}>
                  {currentCondo.contactEmail}
                </a>
              </p>
            )}
            {currentCondo?.contactPhone && (
              <p style={{ margin: '4px 0' }}>
                Phone:{' '}
                <a href={`tel:${currentCondo.contactPhone}`}>
                  {currentCondo.contactPhone}
                </a>
              </p>
            )}
          </div>
        ) : (
          <p style={{ margin: '4px 0' }}>
            Contact details aren’t on file yet. Please reach out to your property
            manager for assistance.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default AtriumOwnerHome;
